import { useLanguage } from '../context/LanguageContext';
import { Instagram, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InstagramFeed = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/instagram/feed`)
      .then(r => {
        if (!r.ok) throw new Error('Failed');
        return r.json();
      })
      .then(data => {
        setPosts(data.posts || []);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  const totalPages = Math.ceil(posts.length / 3);

  const next = useCallback(() => {
    setPage(p => (p + 1) % totalPages);
  }, [totalPages]);

  const prev = useCallback(() => {
    setPage(p => (p - 1 + totalPages) % totalPages);
  }, [totalPages]);

  // Auto-advance every 6 seconds
  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, totalPages]);

  const visiblePosts = posts.slice(page * 3, page * 3 + 3);

  if (error || (!loading && posts.length === 0)) return null;

  return (
    <section 
      className="py-16 md:py-20"
      style={{ backgroundColor: 'var(--warm-white)' }}
      data-testid="instagram-feed"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Instagram size={20} style={{ color: 'var(--sage-600)' }} />
            <a
              href="https://instagram.com/eranmh_coach"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium tracking-wide"
              style={{ color: 'var(--sage-600)' }}
            >
              @eranmh_coach
            </a>
          </div>
          <h2 
            className="text-3xl md:text-4xl"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--charcoal)' }}
          >
            {language === 'he' ? 'מהאינסטגרם שלי' : 'From My Instagram'}
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Nav arrows */}
          {totalPages > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute top-1/2 -translate-y-1/2 -left-2 md:-left-6 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center transition-all hover:shadow-lg hover:scale-105"
                style={{ border: '1px solid var(--sand-200)' }}
                data-testid="ig-prev"
              >
                <ChevronLeft size={20} style={{ color: 'var(--charcoal)' }} />
              </button>
              <button
                onClick={next}
                className="absolute top-1/2 -translate-y-1/2 -right-2 md:-right-6 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center transition-all hover:shadow-lg hover:scale-105"
                style={{ border: '1px solid var(--sand-200)' }}
                data-testid="ig-next"
              >
                <ChevronRight size={20} style={{ color: 'var(--charcoal)' }} />
              </button>
            </>
          )}

          {/* Posts grid */}
          {loading ? (
            <div className="grid grid-cols-3 gap-3 md:gap-5">
              {[0, 1, 2].map(i => (
                <div 
                  key={i}
                  className="aspect-square rounded-2xl animate-pulse"
                  style={{ backgroundColor: 'var(--sand-200)' }}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 md:gap-5">
              {visiblePosts.map((post, i) => (
                <a
                  key={`${page}-${post.id}`}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 ig-card-enter"
                  style={{ animationDelay: `${i * 100}ms` }}
                  data-testid={`instagram-post-${i}`}
                >
                  <img
                    src={post.media_url}
                    alt={post.caption || 'Instagram post'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center p-3">
                      <ExternalLink size={24} className="text-white mx-auto mb-2" />
                      {post.caption && (
                        <p className="text-white text-xs md:text-sm leading-relaxed line-clamp-3">
                          {post.caption}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Dots */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: page === i ? 'var(--sage-600)' : 'var(--sand-300)',
                  transform: page === i ? 'scale(1.3)' : 'scale(1)',
                }}
                data-testid={`ig-dot-${i}`}
              />
            ))}
          </div>
        )}

        {/* Follow CTA */}
        <div className="text-center mt-8">
          <a
            href="https://instagram.com/eranmh_coach"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all hover:scale-105"
            style={{ 
              backgroundColor: 'var(--sage-100)', 
              color: 'var(--sage-700)',
              border: '1px solid var(--sage-200)',
            }}
            data-testid="instagram-follow-btn"
          >
            <Instagram size={16} />
            {language === 'he' ? 'עקבו אחריי באינסטגרם' : 'Follow me on Instagram'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstagramFeed;
